// F1 — vendor archive/unarchive confirm modal (the portal-account archive
// pattern). Archive mode shows the blast radius (active assignments, live
// connections, open invoices + the linked portal account's state) before
// committing; the counts are informational — archiving never mutates them.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import {
  useArchiveVendor,
  useUnarchiveVendor,
  useVendorArchiveImpact,
} from "@/hooks/portfolioAdmin/useRegistries";

export interface VendorArchiveConfirmModalProps {
  open: boolean;
  vendor: VendorDto;
  mode: "archive" | "unarchive";
  onClose: () => void;
}

export function VendorArchiveConfirmModal({
  open,
  vendor,
  mode,
  onClose,
}: VendorArchiveConfirmModalProps) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const archive = useArchiveVendor(vendor.id);
  const unarchive = useUnarchiveVendor(vendor.id);
  const impactQuery = useVendorArchiveImpact(
    open && mode === "archive" ? vendor.id : undefined,
  );
  const impact = impactQuery.data;

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const pending = archive.isPending || unarchive.isPending;

  const submit = async () => {
    try {
      const body = { reason: reason.trim() || null };
      if (mode === "archive") {
        await archive.mutateAsync(body);
        toast.success("Vendor archived.");
      } else {
        await unarchive.mutateAsync(body);
        toast.success("Vendor unarchived.");
      }
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const portalAccountState = impact?.portalAccountIsArchived
    ? "Archived"
    : impact?.portalAccountIsDisabled
      ? "Disabled"
      : "Active";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "archive" ? "Archive vendor" : "Unarchive vendor"}
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={mode === "archive" ? "destructive" : "primary"}
            onClick={submit}
            disabled={pending}
            data-testid="vendor-archive-confirm"
          >
            {pending
              ? "Saving…"
              : mode === "archive"
                ? "Archive vendor"
                : "Unarchive vendor"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        {mode === "archive" ? (
          <>
            <p className="text-small text-gray-dark m-0">
              Archiving hides this vendor from the registry and all pickers
              and blocks new portal invites and status changes. Existing
              assignments, connections, and invoices are not changed.
            </p>
            {impactQuery.isLoading ? (
              <p className="text-small text-gray-mid m-0">Checking impact…</p>
            ) : impact ? (
              <div className="flex flex-col gap-space-1">
                <p
                  className="text-small font-semibold text-onyx m-0"
                  data-testid="vendor-archive-impact-counts"
                >
                  {impact.activeAssignmentCount} active loan assignment
                  {impact.activeAssignmentCount === 1 ? "" : "s"} ·{" "}
                  {impact.liveConnectionCount} live connection
                  {impact.liveConnectionCount === 1 ? "" : "s"} ·{" "}
                  {impact.openInvoiceCount} open invoice
                  {impact.openInvoiceCount === 1 ? "" : "s"}
                </p>
                {impact.hasPortalAccount && impact.portalUserId ? (
                  <p
                    className="text-small text-gray-mid m-0"
                    data-testid="vendor-archive-portal-line"
                  >
                    Linked portal account: {portalAccountState} —{" "}
                    <Link
                      to={`/admin/portal-accounts/${impact.portalUserId}`}
                      className="text-gold-shadow hover:underline"
                    >
                      manage it separately
                    </Link>
                    .
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-small text-gray-dark m-0">
            Unarchiving returns this vendor to the registry and all pickers,
            and re-enables portal invites and status changes.
          </p>
        )}
        <div className="flex flex-col gap-space-1">
          <label
            htmlFor="vendor-archive-reason"
            className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold"
          >
            Reason (optional, audit note)
          </label>
          <Textarea
            id="vendor-archive-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="vendor-archive-reason"
          />
        </div>
      </div>
    </Modal>
  );
}
